// ---------------------------------------------------------------
// Copyright (c) Aspen Publishing. All rights reserved.
// ---------------------------------------------------------------

class ScoreLogin extends HTMLElement {
  constructor() {
    super();

    this.popup = this.closest('popup-component');
    this.steps = this.querySelectorAll('.popup-wrapper');
    this.loginButtons = this.querySelectorAll('.jsBtn-navigate[data-class="login-layout"]');
    this.registerButtons = this.querySelectorAll('.jsBtn-navigate[data-class="register-layout"]');
    this.backButton = this.querySelector('.popup-back.jsBack');

    this._currentStep = parseInt(this.dataset.currentStep || 1);

    this.init();
  }

  init() {
    this.addEventListeners();
    this.showCurrentStep();
    this.setupLoginRedirect();
  }

  get currentStep() {
    return this._currentStep;
  }

  set currentStep(value) {
    this._currentStep = value;
    this.dataset.currentStep = value.toString();
    this.showCurrentStep();
  }

  addEventListeners() {
    this.loginButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.currentStep = 1;
      });
    });

    this.registerButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.currentStep = 2;
      });
    });

    if (this.backButton) {
      this.backButton.addEventListener('click', () => {
        this.currentStep = 1;
      });
    }
  }

  setupLoginRedirect() {
    const loginForm = this.querySelector('login-form');

    if (!loginForm) {
      return;
    }

    const currentUrl = new URL(window.location.href);
    let variantId = currentUrl.searchParams.get('variant');
    const redirectUrl = new URL(currentUrl.origin + currentUrl.pathname);
    const containerQuickShop = document.querySelector('quickshop-component');
    let productId = null;

    if (containerQuickShop) {
      productId = containerQuickShop.dataset.productId;
      variantId = containerQuickShop.currentVariant.id;
    }

    redirectUrl.searchParams.set('score-report', 'verified');

    if (productId) {
      redirectUrl.searchParams.set('score-report-product-id', productId);
    }

    if (variantId) {
      redirectUrl.searchParams.set('variant', variantId);
    }

    loginForm.setAttribute('data-redirect', redirectUrl.toString());
  }

  showCurrentStep() {
    this.steps.forEach((step, index) => {
      if (index + 1 === this.currentStep) {
        step.classList.remove('hide');
      } else {
        step.classList.add('hide');
      }
    });

    if (this.backButton) {
      if (this.currentStep === 2) {
        this.backButton.style.display = '';
      } else {
        this.backButton.style.display = 'none';
      }
    }
  }

  open() {
    if (this.popup) {
      this.popup.setAttribute('aria-hidden', 'false');
      document.body.classList.add('popup-active');
    }
  }

  close() {
    if (this.popup) {
      this.popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('popup-active');
    }
  }
}

if (!customElements.get('score-login')) {
  customElements.define('score-login', ScoreLogin);
}

function isUserAuthenticated() {
  try {
    const cartComponent = document.querySelector('cart-component');

    if (cartComponent && cartComponent.dataset) {
      const isLoggedIn = cartComponent.dataset.customerLogin === 'true';

      return isLoggedIn;
    }
  } catch {
    // Ignorar errores de selección de DOM
  }

  if (window.customer && window.customer.id) {
    return true;
  }

  return false;
}

function hasOnlyLeafProduct() {
  const cartItems = document.querySelectorAll('cart-item');

  if (cartItems && cartItems.length) {
    return Array.from(cartItems).some(item => {
      const platforms = (item.dataset.platforms || '').split(',');

      return platforms.length === 1 && platforms.includes('leaf');
    });
  }

  return false;
}

let _hasSellingPlanInCartCache = null;
let _cartCheckInProgress = false;

async function checkSellingPlanInCart() {
  if (_cartCheckInProgress) {
    return new Promise(resolve => {
      const checkInterval = setInterval(() => {
        if (!_cartCheckInProgress && _hasSellingPlanInCartCache !== null) {
          clearInterval(checkInterval);
          resolve(_hasSellingPlanInCartCache);
        }
      }, 100);
    });
  }

  if (_hasSellingPlanInCartCache !== null) {
    return Promise.resolve(_hasSellingPlanInCartCache);
  }

  _cartCheckInProgress = true;

  try {
    const response = await fetch('/cart.js');

    if (!response.ok) {
      throw new Error(`Error al obtener el carrito: ${response.status}`);
    }

    const cart = await response.json();

    if (!cart.items || !Array.isArray(cart.items)) {
      return false;
    }

    const hasSellingPlan = cart.items.some(item => item.selling_plan_allocation != null);

    _hasSellingPlanInCartCache = hasSellingPlan;

    window.cartData = cart;

    return hasSellingPlan;
  } catch (error) {
    console.error('Error al verificar selling plans:', error);

    return false;
  } finally {
    _cartCheckInProgress = false;
  }
}

// eslint-disable-next-line no-unused-vars
function hasSellingPlanInCart() {
  if (_hasSellingPlanInCartCache !== null) {
    return _hasSellingPlanInCartCache;
  }

  if (!_cartCheckInProgress) {
    checkSellingPlanInCart().then(result => {
      _hasSellingPlanInCartCache = result;
    });
  }

  return false;
}

function removeExistingPopup() {
  const existingPopup = document.querySelector('#cart-login-popup');

  if (existingPopup) {
    document.body.classList.remove('popup-active');
    existingPopup.remove();

    return true;
  }

  return false;
}

function closePopup(popup) {
  document.body.classList.remove('popup-active');

  if (popup) {
    popup.setAttribute('aria-hidden', 'true');
    popup.style.opacity = '0';
    popup.style.visibility = 'hidden';

    setTimeout(() => {
      removeExistingPopup();
    }, 300);
  }
}

async function showCartLoginPopup() {
  removeExistingPopup();

  try {
    document.body.classList.add('loading');

    const response = await fetch('/cart?section_id=popup-cart-login');

    if (!response.ok) {
      throw new Error(`Error al cargar el popup: ${response.status}`);
    }

    const html = await response.text();

    const parts = html.split('<!-- split -->');

    if (parts.length < 2 || !parts[1]) {
      throw new Error('Formato de respuesta incorrecto');
    }

    const popupContent = parts[1];

    document.body.insertAdjacentHTML('beforeend', popupContent);

    const popup = document.querySelector('#cart-login-popup');

    if (!popup) {
      throw new Error('No se pudo encontrar el popup después de insertarlo');
    }

    const popupLayer = popup.querySelector('.popup-layer');
    const popupContentElement = popup.querySelector('.popup__content');

    const closeButtons = popup.querySelectorAll('.jsClose');

    closeButtons.forEach(button => {
      button.addEventListener('click', event => {
        event.preventDefault();
        closePopup(popup);
      });
    });

    if (popupLayer && popupContentElement) {
      popupLayer.style.opacity = '1';

      setTimeout(() => {
        popupContentElement.style.opacity = '1';
        popupContentElement.style.transform = 'translateY(0)';
      }, 50);
    }

    document.body.classList.add('popup-active');
    popup.setAttribute('aria-hidden', 'false');

    document.addEventListener('click', function popupOutsideClick(event) {
      if (
        popup &&
        !event.target.closest('.popup__content') &&
        !event.target.closest('.jsToggle-popup') &&
        !event.target.closest('button[type="submit"]') &&
        !event.target.closest('input')
      ) {
        closePopup(popup);
        document.removeEventListener('click', popupOutsideClick);
      }
    });
  } catch {
    document.body.classList.remove('loading');
  } finally {
    document.body.classList.remove('loading');
  }

  return false;
}

document.addEventListener('DOMContentLoaded', () => {
  const setupCheckoutListeners = () => {
    const checkoutButtons = document.querySelectorAll(
      '#checkout, .checkout-button, .btn-cart-checkout, [href="/checkout"], button[name="checkout"], [name="checkout"], [action="/checkout"]'
    );

    checkoutButtons.forEach(button => {
      if (button.tagName.toLowerCase() !== 'form') {
        const clonedButton = button.cloneNode(true);

        if (button.parentNode) {
          button.parentNode.replaceChild(clonedButton, button);
        }

        clonedButton.addEventListener('click', async function (event) {
          const isAuthenticated = isUserAuthenticated();

          if (!isAuthenticated) {
            event.preventDefault();

            try {
              const hasSellingPlan = await checkSellingPlanInCart();
              const hasLeaf = hasOnlyLeafProduct();

              if (hasSellingPlan || hasLeaf) {
                event.stopPropagation();
                showCartLoginPopup();

                return false;
              } else {
                window.location.href = '/checkout';
              }
            } catch (error) {
              console.error('Error al verificar selling plans:', error);

              return true;
            }
          }

          return true;
        });
      }
    });

    const checkoutForms = document.querySelectorAll('form[action="/checkout"], form#cart');

    checkoutForms.forEach(form => {
      const clonedForm = form.cloneNode(true);

      if (form.parentNode) {
        form.parentNode.replaceChild(clonedForm, form);
      }

      clonedForm.addEventListener('submit', async function (event) {
        const isAuthenticated = isUserAuthenticated();

        if (!isAuthenticated) {
          event.preventDefault();

          try {
            const hasSellingPlan = await checkSellingPlanInCart();

            if (hasSellingPlan) {
              event.stopPropagation();
              showCartLoginPopup();

              return false;
            } else {
              window.location.href = '/checkout';
            }
          } catch (error) {
            console.error('Error al verificar selling plans:', error);

            this.submit();
          }

          return false;
        }

        return true;
      });
    });
  };

  const setupMutationObserver = () => {
    const cartContainer = document.querySelector('cart-items, .cart-container, .cart, #CartDrawer');

    if (cartContainer) {
      const observer = new MutationObserver(() => {
        setupCheckoutListeners();
      });

      observer.observe(cartContainer, { childList: true, subtree: true });
    }
  };

  setupCheckoutListeners();
  setupMutationObserver();
});
